import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Button,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  alpha
} from '@mui/material';
import {
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import DadosPessoaisForm from './DadosPessoaisForm';
import ServicosAtendidosForm from './ServicosAtendidosForm';
import ImageCropper from '../ImageCropper';

/**
 * Componente de diálogo para criação/edição de usuário
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.open - Flag indicando se o diálogo está aberto
 * @param {Function} props.onClose - Função para fechar o diálogo
 * @param {Object} props.formData - Dados do formulário
 * @param {Function} props.setFormData - Função para atualizar os dados do formulário
 * @param {Function} props.onSubmit - Função para enviar o formulário
 * @param {number|null} props.editingId - ID do usuário sendo editado ou null
 * @param {string|null} props.previewImage - URL da imagem de preview
 * @param {boolean} props.cropperOpen - Flag indicando se o cropper está aberto
 * @param {string|null} props.selectedImage - Imagem selecionada para recorte
 * @param {Function} props.handleImageSelect - Função para selecionar imagem
 * @param {Function} props.handleCropComplete - Função para concluir recorte
 * @param {Function} props.handleImageError - Função para tratar erros de imagem
 * @param {Object} props.customTheme - Tema personalizado
 * @param {Array} props.servicosDisponiveis - Lista de serviços disponíveis
 * @param {string} props.searchServico - Termo de busca para serviços
 * @param {Function} props.setSearchServico - Função para atualizar termo de busca
 * @param {Function} props.handleToggleServico - Função para alternar serviço
 * @param {Function} props.handlePrioridadeChange - Função para alterar prioridade
 * @param {boolean} props.carregandoServicos - Flag indicando se serviços estão carregando
 * @param {string|null} props.erroServicos - Mensagem de erro ou null
 * @param {Function} props.carregarServicos - Função para recarregar serviços
 * @returns {JSX.Element}
 */
const UsuarioDialog = ({
  open,
  onClose,
  formData,
  setFormData,
  onSubmit,
  editingId,
  previewImage,
  cropperOpen,
  selectedImage,
  handleImageSelect,
  handleCropComplete,
  handleImageError,
  customTheme,
  servicosDisponiveis,
  searchServico,
  setSearchServico,
  handleToggleServico,
  handlePrioridadeChange,
  carregandoServicos,
  erroServicos,
  carregarServicos,
  setCropperOpen
}) => {
  const [activeStep, setActiveStep] = React.useState(0);

  // Resetar o stepper quando o diálogo é aberto
  React.useEffect(() => {
    if (open) {
      setActiveStep(0);
    }
  }, [open]);

  const handleNextStep = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBackStep = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: theme => theme.palette.mode === 'dark' 
            ? `linear-gradient(135deg, ${alpha(customTheme.palette.primary.dark, 0.8)} 0%, ${alpha(customTheme.palette.primary.main, 0.6)} 100%)` 
            : `linear-gradient(135deg, ${alpha(customTheme.palette.primary.light, 0.6)} 0%, ${alpha(customTheme.palette.primary.main, 0.2)} 100%)`,
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 2.5,
          px: 3,
        }}>
          <Box display="flex" alignItems="center">
            <Avatar 
              sx={{ 
                background: theme => theme.palette.mode === 'dark' 
                  ? customTheme.palette.primary.main 
                  : alpha(customTheme.palette.primary.main, 0.8),
                color: 'white',
                mr: 2
              }}
            >
              <PersonIcon />
            </Avatar>
            <Typography 
              component="span" 
              variant="h6"
              sx={{ 
                fontWeight: 600,
                color: theme => theme.palette.mode === 'dark' ? 'white' : 'text.primary' 
              }}
            >
              {editingId ? 'Editar Usuário' : 'Novo Usuário'}
            </Typography>
          </Box>
        </DialogTitle>

        <Box sx={{ px: 4, pt: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            <Step completed={activeStep > 0}>
              <StepLabel>
                <Typography variant="h6" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  Dados Pessoais
                  <Tooltip title="Preencha os dados pessoais do usuário" arrow placement="right">
                    <InfoIcon sx={{ ml: 1, fontSize: '0.9rem', color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Typography>
              </StepLabel>
            </Step>
            <Step>
              <StepLabel>
                <Typography variant="h6" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  Serviços Atendidos
                  <Tooltip title="Configure os serviços que este usuário pode atender e suas prioridades" arrow placement="right">
                    <InfoIcon sx={{ ml: 1, fontSize: '0.9rem', color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Typography>
              </StepLabel>
            </Step>
          </Stepper>
        </Box>

        <DialogContent sx={{ px: 3, py: 4 }}>
          {activeStep === 0 ? (
            <DadosPessoaisForm 
              formData={formData}
              handleInputChange={handleInputChange}
              handleImageSelect={handleImageSelect}
              previewImage={previewImage}
              handleImageError={handleImageError}
              editingId={editingId}
            />
          ) : (
            <ServicosAtendidosForm 
              formData={formData}
              servicosDisponiveis={servicosDisponiveis}
              searchServico={searchServico}
              setSearchServico={setSearchServico}
              handleToggleServico={handleToggleServico}
              handlePrioridadeChange={handlePrioridadeChange}
              carregandoServicos={carregandoServicos}
              erroServicos={erroServicos}
              carregarServicos={carregarServicos}
              customTheme={customTheme}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between'
        }}>
          {activeStep === 0 ? (
            <>
              <Button 
                onClick={onClose} 
                color="inherit"
                variant="outlined"
                sx={{
                  borderRadius: '10px',
                  py: 1,
                  px: 3,
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'text.secondary'
                  }
                }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleNextStep} 
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: '10px',
                  py: 1,
                  px: 4,
                  background: `linear-gradient(90deg, ${customTheme.palette.primary.main}, ${customTheme.palette.primary.dark})`,
                  boxShadow: `0 4px 14px ${alpha(customTheme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(customTheme.palette.primary.main, 0.6)}`,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Próximo
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={handleBackStep} 
                color="inherit"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: '10px',
                  py: 1,
                  px: 3,
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'text.secondary'
                  }
                }}
              >
                Voltar
              </Button>
              <Button 
                onClick={onSubmit} 
                variant="contained"
                endIcon={<CheckIcon />}
                sx={{
                  borderRadius: '10px',
                  py: 1,
                  px: 4,
                  background: `linear-gradient(90deg, ${customTheme.palette.primary.main}, ${customTheme.palette.primary.dark})`,
                  boxShadow: `0 4px 14px ${alpha(customTheme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(customTheme.palette.primary.main, 0.6)}`,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Salvar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Cropper Dialog */}
      <ImageCropper
        open={cropperOpen}
        image={selectedImage}
        onClose={() => setCropperOpen(false)}
        onComplete={handleCropComplete}
        aspect={1}
        circularCrop
      />
    </>
  );
};

export default UsuarioDialog; 